import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getEnabledDepartmentsConfig } from "../api/configApi";
import { DEFAULT_DEPARTMENT } from "../constants/departments";
import {
  mergeWorkflowConfig,
  resolveDepartmentWorkflow,
  departmentUsesSprints,
  getWorkBoardLabel,
  getCycleLabel,
  getDepartmentNavConfig,
  getDepartmentNavOrder,
} from "../constants/departmentWorkflow";

export function useDepartmentWorkflow() {
  const query = useQuery({
    queryKey: ["enabled-departments"],
    queryFn: getEnabledDepartmentsConfig,
    staleTime: 5 * 60 * 1000,
  });

  /**
   * Memoised on the raw server payload, not recomputed per render.
   * mergeWorkflowConfig builds a fresh object every call, so without this the
   * identity changed on every render — any effect keyed on `workflow` re-ran
   * forever, which silently reset the Departments panel's edit draft.
   */
  const workflow = useMemo(
    () => mergeWorkflowConfig(query.data?.workflow || {}),
    [query.data?.workflow],
  );

  return {
    ...query,
    workflow,
    resolveForDepartment: (department) => resolveDepartmentWorkflow(department, workflow),
    departmentUsesSprints: (department) => departmentUsesSprints(department, workflow),
    getWorkBoardLabel: (department) => getWorkBoardLabel(department, workflow),
    getCycleLabel: (department) => getCycleLabel(department, workflow),
    getNavConfig: (department) => getDepartmentNavConfig(department, workflow),
    getNavOrder: (department) => getDepartmentNavOrder(department, workflow),
    /** True while the saved config is still loading — see SideBar for why. */
    navConfigLoading: query.isPending,
    defaultDepartment: DEFAULT_DEPARTMENT,
  };
}
